import DataGrid, { DataGridConfig } from 'components/DataGrid';
import LoadingScreen from 'components/LoadingScreen';
import PageLayout from 'components/PageLayout';
import history from 'history.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import moment from 'moment';
import React, {
  Suspense,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Row, Col, CardTitle } from 'reactstrap';

import Filters from './Filters';
import RowActions from './RowActions';
import {
  orderbyAtom,
  waListingDataAtom,
  checkedListAtom,
  selectedWebAppsFilterAtom,
  componentHeightAtom,
  agedOutWAColumnKeysAtom,
  DEFAULT_AGED_OUT_WA_LISTING_COLUMNS,
  activeWAColumnKeysAtom,
  deactivatedWAColumnKeysAtom,
  DEFAULT_ACTIVE_WA_LISTING_COLUMNS,
  DEFAULT_DEACTIVED_WA_LISTING_COLUMNS,
  paginationConfigAtom,
  sortOrderAtom,
  forceRerenderUIAtom,
} from './state';
import AgingOutLastDate from '../../components/AgingOutLastDate/AgingOutLastDate';
import { getDrillDownUrl } from '../../helper';
import { WAFilter } from '../config';
import { appliedWAIdsAtom, waRefreshAtom } from '../state';

// const BASE_URL: string = getItemFromLocalStorage('approle') || '';

const Listing = (_props: Props) => {
  const appliedRowIds = useAtomValue(appliedWAIdsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);

  // NEW STATES
  const listingData = useAtomValue(waListingDataAtom);
  const [orderBy, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const [loading, _setLoading] = useState(false);

  const [activeWAColumnKeys, updateActiveWAColumnKeys] = useAtom(
    activeWAColumnKeysAtom,
  );
  const [deactivatedWAColumnKeys, updateDeactivatedWAColumnKeys] = useAtom(
    deactivatedWAColumnKeysAtom,
  );
  const [agedOutWAColumnKeys, updateAgedOutWAColumnKeys] = useAtom(
    agedOutWAColumnKeysAtom,
  );

  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const selectedWebAppsFilter = useAtomValue(selectedWebAppsFilterAtom);
  const targetRef = useRef<HTMLDivElement>(null);
  const setComponentHeight = useSetAtom(componentHeightAtom);

  const setWARefresh = useSetAtom(waRefreshAtom);
  const forceRerenderUI = useAtomValue(forceRerenderUIAtom);
  const enableSelectionAcrossPages = true;

  useLayoutEffect(() => {
    if (targetRef.current) {
      setComponentHeight(targetRef.current.offsetHeight);
    }
  }, []);

  const isInactiveOrDeletedFilterSelected = () => {
    return (
      selectedWebAppsFilter === WAFilter.DELETED ||
      selectedWebAppsFilter === WAFilter.INACTIVE
    );
  };

  const isWebAppAgingOutInThirtyDays = (rowData) => {
    const daysDiff = moment().diff(
      moment(rowData['last_deactivated_at']),
      'days',
    );

    // Calculate remaining days until aging out
    const agingOutTotalDays = 15 * 30;
    const remainingDays = agingOutTotalDays - daysDiff;

    // Return true if remaining days is within 30 days
    return remainingDays <= 30 && selectedWebAppsFilter === WAFilter.INACTIVE;
  };

  const getColumnsDisplayConfig = () => {
    if (selectedWebAppsFilter === WAFilter.DELETED) {
      return {
        enabledColumnKeys: agedOutWAColumnKeys,
        defaultColumnKeys: DEFAULT_AGED_OUT_WA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'vuln',
          'webapp_is_scheduled',
          'results_status',
          'auth_status',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateAgedOutWAColumnKeys(updatedColumnKeys);
        },
      };
    } else if (selectedWebAppsFilter === WAFilter.INACTIVE) {
      return {
        enabledColumnKeys: deactivatedWAColumnKeys,
        defaultColumnKeys: DEFAULT_DEACTIVED_WA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'deleted_webapp__open_vuln_counts',
          'deleted_webapp__closed_vuln_counts',
          'deleted_webapp__deleted_at',
          'deleted_webapp__deleted_by',
          'webapp_created_date',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateDeactivatedWAColumnKeys(updatedColumnKeys);
        },
      };
    } else {
      return {
        enabledColumnKeys: activeWAColumnKeys,
        defaultColumnKeys: DEFAULT_ACTIVE_WA_LISTING_COLUMNS,
        keysToBeExcluded: [
          'deleted_webapp__open_vuln_counts',
          'deleted_webapp__closed_vuln_counts',
          'deleted_webapp__deleted_at',
          'deleted_webapp__deleted_by',
          'deactivated_by',
          'last_deactivated_at',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          updateActiveWAColumnKeys(updatedColumnKeys);
        },
      };
    }
  };

  const getGridConfig = (): DataGridConfig => {
    const config: DataGridConfig = {
      rowIdKey: 'id',
      columns: [
        {
          key: 'id',
          label: 'Id',
          sortable: true,
          headerTooltip: 'Row identifier in SP360 database',
        },
        {
          key: 'webapp_id',
          label: 'Qualys ID',
          sortable: true,
          headerTooltip: 'WebApp ID at Qualys',
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          headerTooltip: 'Name of the webapp',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
        },
        {
          key: 'url',
          label: 'URL',
          sortable: true,
          headerTooltip: 'URL of the webapp',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
        },
        {
          key: 'webapp_is_scheduled',
          label: 'Scheduled',
          sortable: true,
          headerTooltip:
            'The scheduled flag indicates whether the webapp is scheduled to be scanned',
        },
        {
          key: 'vuln',
          label: 'Vuln Count',
          sortable: true,
          headerTooltip: 'Total number of vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.vuln}
            </label>
          ),
          onClick: (rowData) => {
            const isActive = rowData['is_active'];
            if (isActive) {
              history.push(
                getDrillDownUrl({
                  type: 'open',
                  sourceType: 'was_data',
                  hosts_in: [rowData.id],
                  severity: '',
                  issueType: '',
                  qid: '',
                  severity_in: undefined,
                }),
                // `/${BASE_URL}/qualys/vulnerabilities?source_type=was_data&webapp=${rowData.id}`,
              );
            }
          },
        },
        {
          key: 'webapp_created_date',
          label: 'Created At',
          sortable: true,
          headerTooltip: 'Creation date of the webapp',
          render: (rowData) =>
            rowData['last_scan_date']?.length ? (
              <span className="ml-2">
                {rowData['webapp_created_date']?.length
                  ? moment(rowData['webapp_created_date']).format('lll')
                  : null}
              </span>
            ) : (
              <AgingOutLastDate
                lastDate={rowData['webapp_created_date']}
                totalDays={6 * 30}
                warningDaysDiff={3 * 30}
                dangerDaysDiff={1 * 30}
                tooltipTitle="Deactivating"
                showTooltipOnWarningOrDanger={false}
              />
            ),
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
        },
        {
          key: 'last_scan_date',
          label: 'Last Scanned',
          sortable: true,
          headerTooltip: 'Last scan date of the webapp',
          render: (rowData) =>
            selectedWebAppsFilter === WAFilter.INACTIVE ? (
              <span className="ml-2">
                {rowData['last_scan_date']?.length
                  ? moment(rowData['last_scan_date']).format('lll')
                  : null}
              </span>
            ) : (
              <AgingOutLastDate
                lastDate={rowData['last_scan_date']}
                totalDays={1 * 30}
                dangerDaysDiff={10}
                warningDaysDiff={20}
                tooltipTitle="Deactivating"
                showTooltipOnWarningOrDanger={false}
              />
            ),
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
        },
        {
          key: 'results_status',
          label: 'Scan Result',
          sortable: true,
          headerTooltip: 'Scan result of the webapp',
          overrideContents: (rowData) => {
            const status = rowData.results_status;
            if (!status) return null;

            const statusMap = {
              FINISHED: { label: 'Finished' },
              SUCCESSFUL: { label: 'Finished' },
              RUNNING: { label: 'Running' },
              FAILED: { label: 'Failed', className: 'text-critical' },
              ERROR: { label: 'Error', className: 'text-critical' },
              CANCELED: { label: 'Canceled' },
            };

            const { label, className = '' } = statusMap[status] || {
              label: status.replaceAll('_', ' ').toLowerCase(),
              className: '',
            };

            return (
              <div className="d-flex align-items-center">
                <span
                  className={className}
                  style={{ textAlign: 'center', textTransform: 'capitalize' }}
                >
                  {label}
                </span>
              </div>
            );
          },
        },
        {
          key: 'auth_status',
          label: 'Auth Status',
          sortable: true,
          headerTooltip: 'Authentication status of the webapp',
          tooltip: (rowData) => {
            if (rowData.auth_status === 'FAILED') {
              return 'Failed to authenticate. Click here to contact support';
            }
            return null;
          },
          overrideContents: (rowData) => {
            const status = rowData.auth_status;
            if (!status) return null;

            const statusMap = {
              SUCCESSFUL: { label: 'Success', className: '' },
              FAILED: { label: 'Failed', className: 'text-critical' },
              NOT_USED: { label: 'Not Used', className: '' },
              NONE: { label: 'None', className: '' },
            };

            const { label, className } = statusMap[status] || {
              label: status.replaceAll('_', ' ').toLowerCase(),
              className: '',
            };

            return (
              <div className="d-flex align-items-center">
                <span
                  className={className}
                  style={{ textAlign: 'center', textTransform: 'capitalize' }}
                >
                  {status === 'FAILED' ? (
                    <a
                      href="mailto:support@securepoint360.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'inherit',
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                      }}
                    >
                      {label}
                    </a>
                  ) : (
                    label
                  )}
                </span>
              </div>
            );
          },
        },
        {
          key: 'deactivated_by',
          label: 'Deactivated By',
          sortable: true,
          headerTooltip: 'User who deactivated the webapp',
        },
        {
          key: 'last_deactivated_at',
          label: 'Deactivated At',
          sortable: true,
          headerTooltip: 'Deactivation date of the webapp',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
          render: (rowData) =>
            selectedWebAppsFilter === WAFilter.INACTIVE ? (
              <AgingOutLastDate
                lastDate={rowData['last_deactivated_at']}
                totalDays={15 * 30}
                dangerDaysDiff={1 * 30}
                warningDaysDiff={3 * 30}
                showTooltipOnWarningOrDanger={false}
              />
            ) : (
              <span className="ml-2">
                {rowData['last_deactivated_at']?.length
                  ? moment(rowData['last_deactivated_at']).format('lll')
                  : null}
              </span>
            ),
        },
        {
          key: 'deleted_webapp__open_vuln_counts',
          label: 'Open Vuln Counts',
          sortable: true,
          headerTooltip: 'Total number of open vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_webapp__open_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_webapp__closed_vuln_counts',
          label: 'Close Vuln Counts',
          sortable: true,
          headerTooltip: 'Total number of closed vulnerabilities',
          overrideContents: (rowData) => (
            <label
              className="label btn slim"
              style={{ color: 'rgb(255, 255, 255)', cursor: 'pointer' }}
            >
              {rowData.deleted_webapp__closed_vuln_counts}
            </label>
          ),
        },
        {
          key: 'deleted_webapp__deleted_at',
          label: 'Delete At',
          sortable: true,
          headerTooltip: 'Deletion date of the webapp',
        },
        {
          key: 'deleted_webapp__deleted_by',
          label: 'Delete By',
          sortable: true,
          headerTooltip: 'User who deleted the webapp',
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => <RowActions rowData={rowData} />,
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '100px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '100px !important',
            maxWidth: 'auto',
          },
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      rowConfig: {
        classRules: [
          {
            className: 'warning-row',
            ruleFunction: isWebAppAgingOutInThirtyDays,
          },
          {
            className: 'applied-row',
            ruleFunction: (rowData) =>
              rowData['isAppliedForDashboard'] === true,
          },
        ],
      },
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1, // page number expects a zero based index
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          // console.log("page number changed to " + newPage);
          // page number returns a zero based index, so incrementing by 1
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });
          if (!enableSelectionAcrossPages) {
            // reset selected row ids on  change
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          // console.log("page size changed to " + newPageSize);
          // reset page number on page size change
          setPaginationConfig({ page_size: newPageSize, page: 1 });

          // reset selected row ids on  change
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },

      // add more configuration options for responsive columns display, for different screen sizes
    };

    // Conditionally add row selection configuration, should not be available for deleted EP's
    config.rowSelectionConfig = isInactiveOrDeletedFilterSelected()
      ? undefined
      : {
          enableSelectionAcrossPages: enableSelectionAcrossPages,
          preSelectedRows: selectedRowIds,
          onSelectionChange: (updatedRowIds) => {
            setSelectedRowIds(updatedRowIds);
          },
        };

    return config;
  };

  const config = useMemo<DataGridConfig>(() => {
    return getGridConfig();
  }, [
    listingData,
    activeWAColumnKeys,
    deactivatedWAColumnKeys,
    agedOutWAColumnKeys,
    setWARefresh,
    forceRerenderUI,
    paginationConfig,
    selectedRowIds,
    appliedRowIds,
    selectedWebAppsFilter,
  ]);

  return (
    <div ref={targetRef}>
      <PageLayout
        id="webapps_listing"
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  WebApps
                  <Suspense fallback={<></>}>
                    <label className="label btn">
                      {listingData?.count || 0}
                    </label>
                  </Suspense>
                </h4>
              </CardTitle>
            </Col>
          </Row>
        }
        description="List of WebApps."
        filteringOptions={
          <Suspense fallback={<LoadingScreen />}>
            <Filters />
          </Suspense>
        }
      >
        <Suspense fallback={<LoadingScreen />}>
          <DataGrid
            config={config}
            data={listingData?.items}
            loading={loading}
          />
        </Suspense>
      </PageLayout>
    </div>
  );
};

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const WAList = ({ setModalComponentData }: Props) => {
  const componentHeight = useAtomValue(componentHeightAtom);

  return (
    <Suspense fallback={<LoadingScreen height={componentHeight} />}>
      <Listing setModalComponentData={setModalComponentData} />
    </Suspense>
  );
};

export default WAList;
